import { log } from "../utils.js";

export const RTCSignalType = Object.freeze({
    OFFER: "offer",
    ANSWER: "answer",
    ICE_CANDIDATE: "candidate",
});


/**
 * Class representing a RTC peer connection with a single data channel.
 */
export class RTCPeer {
    /**
     * @param {RTCConfiguration} rtcConfig
     * @param {object} dataChannelListeners An object whose keys are the event
     *      names and the respective values are the according event handler 
     *      functions.
     * @param {(event: Event) => any =} dataChannelListeners.open
     * @param {(msgEvent: MessageEvent) => any =} dataChannelListeners.message
     * @param {(event: Event) => any =} dataChannelListeners.close
     */
    constructor(rtcConfig, dataChannelListeners) {
        /** @type RTCDataChannel? */
        this.dataChannel = null;

        this._rtcConfig = rtcConfig;
        this._rtcPeerConnection = new RTCPeerConnection(this._rtcConfig);
        this._dataChannelEventListeners = dataChannelListeners;
    }

    get connection() { return this._rtcPeerConnection; }

    /**
     * Resets the peer to a state similar to the initial construction.
     * Any existing connections will be closed. 
     */
    reset() {
        this.close();
        this._rtcPeerConnection = new RTCPeerConnection(this._rtcConfig);
        this.dataChannel = null;
    }

    /**
     * Defines how the dataChannel will handle events and thus react to received data.
     * @param {RTCDataChannel} dataChannel 
    */
    setupDataChannel(dataChannel) {
        this.dataChannel = dataChannel;

        // Setup default event listeners
        dataChannel.onopen = () => log("[RTCPeer] DataChannel open.");
        dataChannel.onmessage =
            (e) => log(`[RTCPeer] Received message from peer: ${JSON.stringify(e.data)}`);
        dataChannel.onclose = () => log("[RTCPeer] DataChannel closed.");

        // Attach externally provided event handlers
        for (const eventName in this._dataChannelEventListeners) {
            if (this._dataChannelEventListeners.hasOwnProperty(eventName)) {
                const listener = this._dataChannelEventListeners[eventName];
                this.dataChannel.addEventListener(eventName, listener);
            }
        }
    }

    /**
     * Close peer connection and release other associated resources.
     */
    close() {
        this.dataChannel?.close();
        this.connection.close();
    }
}
